<template>
    <transition
        :css="false"
        @enter="intro"
        @leave="outro"
    >
        <div class="TextBarrel">
            <div
                class="TextBarrel-container"
                :style="containerStyle"
            >
                <div class="TextBarrel-origin" :style="originStyle">
                    <!--
                    Depth could be defined directly on the `face` elements by
                    setting the Z transform origin style on them:
                    `transform-origin: 50% 50% <depth>px`
                    but that won't work on Safari:
                    https://stackoverflow.com/a/12785099/3841699
                    -->
                    <div class="TextBarrel-depth" :style="depthStyle">
                        <div
                            v-for="index in faces"
                            :key="index"
                            ref="face"
                            class="TextBarrel-face"
                            :style="faceStyle(index)"
                        >
                            <svg
                                class="TextBarrel-caption"
                                :viewBox="captionViewBox"
                            >
                                <text x="50%" y="50%">
                                    {{activeText[index - 1]}}
                                </text>
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
            <!--
            Container (which contains 3d objects) and curtain must be separated
            (siblings) because of Safari and iOS z-index issues
            -->
            <div class="TextBarrel-curtain"></div>
        </div>
     </transition>
</template>

<script>
import { oneLine } from 'common-tags'
import { map, interpolate, degreesToRadians } from '@/foo/utils/MathUtils'
import app from '@/app'

export default {
    name: 'TextBarrel',
    props: {
        introDelay: {
            type: Number,
            defautl: 0,
        },
        introDuration: {
            type: Number,
            default: 3,
        },
        outroDelay: {
            type: Number,
            default: 0,
        },
        outroDuration: {
            type: Number,
            default: 0.75,
        },
        text: {
            type: Array,
            required: true,
        },
        /**
         * Relation between this `$el` height and perspective, being 1 the total
         * height set as the perspective.
         */
        perspectiveRatio: {
            type: Number,
            default: 1,
        },
        /**
         * Relation between this `$el` height and depth, being 1 the total
         * height set as the depth.
         */
        depthRatio: {
            type: Number,
            default: -0.8,
        },
        faces: {
            default: 7,
            validator(val) {
                if (val % 2 === 0 || val < 3) {
                    console.error(oneLine`${val} is not a valid number for faces
                    prop, number must be odd and at least 3 faces are needed.`)
                    return false
                }
                return true
            },
        },
        acceleration: {
            type: Number,
            default: 0.1,
        },
        maxVelocity: {
            type: Number,
            default: 2,
        },
        tiltMaxAngle: {
            type: Number,
            default: degreesToRadians(10),
        },
        tiltFriction: {
            type: Number,
            default: 0.05,
        },
    },
    data() {
        return {
            activeText: [],
            textIndex: 0,
            perspective: 0,
            depth: 0,
            angle: 0,
            maxAngle: 0,
            velocity: 0,
            isAnimating: false,
            faceSize: {
                width: 0,
                height: 0,
            },
            animProgress: [
            ],
            mouse: {
                x: 0,
                y: 0,
            },
            tilt: {
                x: 0,
                y: 0,
                angleX: 0,
                angleY: 0,
            },
        }
    },
    methods: {
        intro($el, done) {
            const { introDuration, introDelay } = this
            // Init before animations starts
            this.init()
            TweenLite.to($el, introDuration, {
                alpha: 1,
                delay: introDelay,
                onStart: () => {
                    // .. but animate until animation begins.
                    this.isAnimating = true
                },
                onComplete: done,
            })
        },
        outro($el, done) {
            const { outroDuration, outroDelay } = this
            TweenLite.to($el, outroDuration, {
                alpha: 0,
                ease: Cubic.easeOut,
                delay: outroDelay,
                onComplete: () => {
                    // Stop and unbind when animation ends
                    this.destroy()
                    done()
                },
            })
        },
        init() {
            this.setActiveText()
            this.update()
            this.setAnimProgress()
            app.resize.add(this.update)
            app.render.add(this.animate)
            window.addEventListener('mousemove', this.onMouseMove)
        },
        destroy() {
            this.isAnimating = false
            app.resize.remove(this.update)
            app.render.remove(this.animate)
            window.removeEventListener('mousemove', this.onMouseMove)
        },
        getAnimProgressFromAngle(angle) {
            const { maxAngle } = this
            return map(angle, maxAngle, -maxAngle, 0, 1)
        },
        getAngleFromAnimProgress(progress) {
            const { maxAngle } = this
            return interpolate(progress, maxAngle, -maxAngle)
        },
        update() {
            this.setPerspective()
            this.setDepth()
            this.setSize()
            this.setAngle()
            this.setMaxAngle()
        },
        setAnimProgress(index) {
            const { faces, animProgress, angle, faceRange } = this
            for (let i = 1; i <= faces; i++) {
                // End to center index
                const index = map(i, 1, faces, faceRange, -faceRange)
                // Face initial angle
                const startAngle = angle * index
                // From that angle get its animation progress
                const animStart = this.getAnimProgressFromAngle(startAngle)
                animProgress.push(animStart)
            }
        },
        animate() {
            if (this.isAnimating) {
                let { animProgress, velocity } = this
                this.animProgress = animProgress.map((progress, index) => {
                    // Reset progress if it reaches 0, which means a loop is
                    // completed
                    if (progress <= 0) {
                        this.updateActiveText(index)
                        return 1
                    }
                    return progress - velocity
                })
                this.updateVelocity()
                this.updateTilt()
            }
        },
        updateActiveText(index) {
            const { textIndex, text, activeText } = this
            this.textIndex = textIndex < text.length - 1 ? textIndex + 1 : 0
            // Only update the text of the face that completed its loop
            activeText[index] = text[this.textIndex]
        },
        updateVelocity() {
            const { acceleration, maxVelocity } = this
            const max = maxVelocity * 0.001
            if (this.velocity < max) {
                // Increase velocity with accelaration
                this.velocity += acceleration * 0.0001
                // Ease accelaration
                this.velocity = interpolate(0.001, this.velocity, max)
                // Clamp velocity
                this.velocity = Math.min(this.velocity, max)
            }
        },
        updateTilt() {
            const { $el, tilt, mouse, tiltMaxAngle, tiltFriction } = this
            let maxX = $el.offsetWidth / 2
            let maxY = $el.offsetHeight / 2
            tilt.x = interpolate(tiltFriction, tilt.x, mouse.x)
            tilt.y = interpolate(tiltFriction, tilt.y, mouse.y)
            let x = maxX - tilt.x
            let y = maxY - tilt.y
            tilt.angleX = map(y, -maxY, maxY, -tiltMaxAngle, tiltMaxAngle)
            tilt.angleY = map(x, -maxX, maxX, tiltMaxAngle, -tiltMaxAngle)
        },
        setActiveText() {
            const { faces, text } = this
            this.activeText = text.filter((line, index) => index < faces)
            this.textIndex = this.activeText.length - 1
        },
        setPerspective() {
            const { $el, perspectiveRatio } = this
            this.perspective = Math.round($el.offsetHeight * perspectiveRatio)
        },
        setDepth() {
            const { $el, depthRatio } = this
            this.depth = Math.round($el.offsetHeight * depthRatio)
        },
        setSize() {
            const { $refs, faceSize } = this
            const firstFace = $refs.face[0]
            faceSize.width = firstFace.offsetWidth
            faceSize.height = firstFace.offsetHeight
        },
        setAngle() {
            const { depth, faceSize } = this
            this.angle = -Math.asin(faceSize.height / 2 / depth)
            this.angle *= 2
        },
        setMaxAngle() {
            const { angle, faceRange } = this
            this.maxAngle = angle / 2
            this.maxAngle += angle * faceRange
        },
        faceStyle(index) {
            const { depth, animProgress } = this
            const progress = animProgress[index - 1]
            const angle = this.getAngleFromAnimProgress(progress)
            return {
                'transform': `rotateX(${angle}rad) translateZ(${-depth}px)`,
            }
        },
        onMouseMove(ev) {
            const { mouse } = this
            mouse.x = ev.clientX
            mouse.y = ev.clientY
        },
    },
    computed: {
        containerStyle() {
            const { perspective } = this
            return {
                'perspective': `${perspective}px`,
            }
        },
        originStyle() {
            const { angleX, angleY } = this.tilt
            return {
                'transform': `rotateX(${angleX}rad) rotateY(${angleY}rad)`
            }
        },
        depthStyle() {
            const { depth } = this
            return {
                transform: `translateZ(${depth}px)`,
            }
        },
        faceRange() {
            const { faces } = this
            return ~~(faces / 2)
        },
        captionViewBox() {
            const { width, height } = this.faceSize
            return `0 0 ${width} ${height}`
        },
    },
}
</script>

<style src="styles/components/TextBarrel.styl" lang="stylus"></style>


// WEBPACK FOOTER //
// src/app/components/TextBarrel.vue